/**
 * Record a dispute and Resolve a dispute (docs/features/v1-completion/L_credit_control_spec.md §3.3; mock-up
 * L_credit_control.html states 3 and 5): the two writes finance makes on a customer's challenge to an invoice,
 * from a payer's open invoices, the Disputes tab and the invoice page.
 *
 * Each asks for one internal text (1 to 500 characters), says what it will do in the approved words, and binds
 * the server's refusal where it belongs (api-validation.md): a missing or over-long reason or note under its
 * input, anything else ("already disputed", "settled in Xero") in the banner, in the server's own words. The
 * dialog owns the fields and the refusal; `onSubmit` does the write and throws when it is refused.
 */
import { useId, useState } from "react";
import { disputeFailure } from "@shared-core/domain/credit-control-presentation";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";

const MAX_TEXT_LENGTH = 500;

function Facts({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-sm">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-xs text-muted-foreground">{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The one text a dispute dialog asks for, with the server's refusal under it. */
function TextField({
  label,
  hint,
  value,
  onChange,
  fieldMessage,
}: {
  label: string;
  hint: string | null;
  value: string;
  onChange: (value: string) => void;
  fieldMessage: string | null;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}{" "}
        <span className="font-normal text-muted-foreground">
          (up to {MAX_TEXT_LENGTH} characters)
        </span>
      </Label>
      <Textarea
        id={id}
        rows={3}
        value={value}
        aria-invalid={fieldMessage ? true : undefined}
        aria-describedby={
          [fieldMessage ? errorId : null, hint ? hintId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        onChange={(e) => onChange(e.target.value)}
      />
      {fieldMessage && (
        <p id={errorId} className="text-sm text-red-600">
          {fieldMessage}
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}

export interface RecordableInvoice {
  invoiceNo: string;
  orderNo: string | null;
  /** ISO date the invoice fell due. */
  dueOn: string;
  payerName: string;
  /** The amount shown in the facts, with its caption ("Owed" on aged debt, "Invoice total" on the invoice page). */
  amountPence: number;
  amountLabel: string;
}

export function RecordDisputeDialog({
  invoice,
  onClose,
  onSubmit,
}: {
  invoice: RecordableInvoice;
  onClose: () => void;
  /** Records the dispute; throws when it is refused. The page re-reads afterwards. */
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ReturnType<
    typeof disputeFailure
  > | null>(null);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a dispute</DialogTitle>
          <DialogDescription>
            The customer is challenging this invoice. It shows as Disputed on
            aged debt and the invoice until you resolve it.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              await onSubmit(reason);
              onClose();
            } catch (err) {
              setFailure(disputeFailure(err, "reason"));
            } finally {
              setSaving(false);
            }
          }}
        >
          {failure?.banner && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{failure.banner}</AlertDescription>
            </Alert>
          )}
          <Facts
            rows={[
              {
                label: "Invoice",
                value: [
                  invoice.invoiceNo,
                  invoice.orderNo,
                  `due ${formatShortDateOrDash(invoice.dueOn)}`,
                ]
                  .filter(Boolean)
                  .join(" · "),
              },
              { label: "Payer", value: invoice.payerName },
              {
                label: invoice.amountLabel,
                value: formatQuotedPrice(invoice.amountPence),
              },
            ]}
          />
          <TextField
            label="What is the customer saying?"
            hint="Internal: it never appears on the customer's copy or in a ticket."
            value={reason}
            onChange={setReason}
            fieldMessage={failure?.fieldMessage ?? null}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              Record dispute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface ResolvableDispute {
  invoiceNo: string;
  /** The invoice's amount when the page knows it. */
  amountPence: number | null;
  raisedAt: string;
  raisedByName: string | null;
  reason: string;
}

export function ResolveDisputeDialog({
  dispute,
  onClose,
  onSubmit,
}: {
  dispute: ResolvableDispute;
  onClose: () => void;
  /** Resolves the dispute; throws when it is refused. The page re-reads afterwards. */
  onSubmit: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ReturnType<
    typeof disputeFailure
  > | null>(null);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve dispute</DialogTitle>
          <DialogDescription>
            A resolved dispute is never reopened: a new challenge is a new
            dispute.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              await onSubmit(note);
              onClose();
            } catch (err) {
              setFailure(disputeFailure(err, "note"));
            } finally {
              setSaving(false);
            }
          }}
        >
          {failure?.banner && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{failure.banner}</AlertDescription>
            </Alert>
          )}
          <Facts
            rows={[
              {
                label: "Invoice",
                value:
                  dispute.amountPence === null
                    ? dispute.invoiceNo
                    : `${dispute.invoiceNo} · ${formatQuotedPrice(dispute.amountPence)}`,
              },
              {
                label: "Raised",
                value: [
                  formatShortDateOrDash(dispute.raisedAt),
                  dispute.raisedByName ? `by ${dispute.raisedByName}` : null,
                ]
                  .filter(Boolean)
                  .join(" "),
              },
              { label: "Reason", value: dispute.reason },
            ]}
          />
          <TextField
            label="How was it resolved?"
            hint={`For example "Credit note CN-K03112 raised for 500 flyers" or "Customer withdrew it".`}
            value={note}
            onChange={setNote}
            fieldMessage={failure?.fieldMessage ?? null}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              Resolve dispute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
